import fp from 'lodash/fp';

const rootPaths = {
  app: '/',
  authenticate: '/authenticate',
  confirmAcceptInvitation: '/confirm-accept-invitation',
  externalCpd: '/external-cpd',
  forgotPassword: '/forgot-password',
  info: '/info',
  loggedOut: '/logged-out',
  login: '/login',
  logout: '/logout',
  onboarding: '/onboarding',
  professionalProfile: '/professional-profile',
  publisherPortal: '/publisher',
  adminPortal: '/admin',
  businessLibrary: '/library',
  resetPassword: '/reset-password',
  settings: '/settings',
  teams: '/teams',
  training: '/education',
  tasks: '/tasks',
  events: '/events',
  referral: '/referral',
  qualification: '/qualification'
};

const settingsTabs = {
  businessDetails: 'business-details',
  loginDetail: 'login',
  paymentDetail: 'payment',
  personalDetail: 'personal',
  trainingPlan: 'training-plan',
  businessTrainingPlan: 'business-training-plan',
  allTrainingPlans: 'all-training-plans',
  teamTrainingPlans: 'team-training-plans',
  userManagement: 'user-management',
  teams: 'teams',
  integration: 'integration',
  invoices: 'invoices'
};

export const tabKeys = {
  accountSettings: {
    personalDetail: settingsTabs.personalDetail,
    loginDetail: settingsTabs.loginDetail,
    paymentDetail: settingsTabs.paymentDetail,
    businessDetails: settingsTabs.businessDetails,
    integration: settingsTabs.integration,
    invoices: settingsTabs.invoices
  },
  usersAndTeamsSettings: {
    userManagement: settingsTabs.userManagement,
    teams: settingsTabs.teams
  },
  trainingPlansSettings: {
    userTrainingPlan: settingsTabs.trainingPlan,
    businessTrainingPlan: settingsTabs.businessTrainingPlan,
    allTrainingPlans: settingsTabs.allTrainingPlans,
    teamTrainingPlans: settingsTabs.teamTrainingPlans
  },
  businessUserSettings: {
    trainingPlan: settingsTabs.trainingPlan
  },
  businessTeamSettings: {
    trainingPlan: settingsTabs.trainingPlan
  }
};

export const createPath = fp.flow(fp.filter(fp.identity), fp.join('/'));
export const nestedPath = (rootPath) => fp.flow(
  fp.map(fp.trimChars(['/'])),
  fp.concat([rootPath]),
  createPath
);

const paths = {
  externalCpd: nestedPath(rootPaths.externalCpd),
  professionalProfile: nestedPath(rootPaths.professionalProfile),
  publisherPortal: nestedPath(rootPaths.publisherPortal),
  library: nestedPath(rootPaths.businessLibrary),
  onboarding: nestedPath(rootPaths.onboarding),
  teams: nestedPath(rootPaths.teams),
  training: nestedPath(rootPaths.training),
  events: nestedPath(rootPaths.events),
  adminPortal: nestedPath(rootPaths.adminPortal),
  tasks: nestedPath(rootPaths.tasks),
  qualification: nestedPath(rootPaths.qualification)
};

export const routes = {
  app: {
    root: rootPaths.app,
    dashboard: rootPaths.app,
    confirmAcceptInvitation: rootPaths.confirmAcceptInvitation,
    info: rootPaths.info, // TODO: Remove when all info is displayed in it's proper place
    healthCheck: '/healthcheck',
    userNotFound: '/user-not-found',
    library: {
      root: rootPaths.businessLibrary,
      content: paths.library(['content']),
      contentDetails: paths.library(['content', 'details']),
      contentQuestion: paths.library(['content', 'question']),
      completedContent: paths.library(['content', 'completed'])
    },
    training: {
      home: rootPaths.training,
      technicalHome: paths.training(['technical']),
      eventHome: rootPaths.events,
      businessContentHome: paths.training(['business-content']),
      bookmark: paths.training(['bookmark']),
      cpdCategory: (cpdCategoryId) => paths.training(['area-of-advice', cpdCategoryId]),
      goal: (goalId) => paths.training(['goal', goalId]),
      eventCpdCategory: (cpdCategory) => paths.events(['area-of-advice', cpdCategory]),
      content: paths.training(['content']),
      nonCpdContent: paths.training(['non-cpd-content']),
      contentAlt: (contentId) => paths.training(['content', contentId]),
      event: paths.training(['event'])
    },
    tasks: {
      home: rootPaths.tasks,
      chooseTaskType: paths.tasks(['choose-task-type']),
      chooseCpdAllocation: paths.tasks(['choose-cpd-allocation']),
      allTasks: paths.tasks(['all-tasks']),
      details: paths.tasks(['details']),
      review: paths.tasks(['review']),
      view: (taskId) => paths.tasks(['view', taskId]),
      edit: (taskId) => paths.tasks(['edit', taskId])
    },
    accountSettings: (tab) => createPath([rootPaths.settings, tab]),
    businessSettings: (tab) => createPath([rootPaths.settings, 'business', tab]),
    usersAndTeamsSettings: (tab) => createPath([rootPaths.settings, 'users-and-teams', tab]),
    trainingPlansSettings: (tab) => createPath([rootPaths.settings, 'training-plans', tab]),
    professionalProfile: {
      root: rootPaths.professionalProfile,
      cpdActivity: paths.professionalProfile(['cpd-activity']),
      completedTasks: paths.professionalProfile(['completed-tasks']),
      progressReport: paths.professionalProfile(['progress-report']),
      snapshotProgressReport: (snapshotId) => paths.professionalProfile(['snapshot', snapshotId, 'progress-report']),
      content: paths.professionalProfile(['content']),
      qualificationDetail: paths.professionalProfile(['qualification-detail']),
      qualificationEdit: (qualificationId) => paths.professionalProfile(['qualification-detail', qualificationId]),
      businessUserQualificationDetail: (businessId, userId, qualificationId) => paths.professionalProfile(['business', businessId, 'user', userId, 'qualification-detail', qualificationId])
    },
    externalCpd: {
      root: rootPaths.externalCpd,
      details: paths.externalCpd(['details']),
      registerEvent: (eventId) => paths.externalCpd(['registerEvent', eventId]),
      review: paths.externalCpd(['review']),
      reviewTeam: (teamId) => paths.externalCpd(['review', 'team', teamId]),
      reviewTeamMember: (teamId, userId) => paths.externalCpd(['review', 'team', teamId, 'user', userId]),
      detailsEdit: (externalCpdId) => paths.externalCpd(['details', externalCpdId]),
      detailsReview: (externalCpdId, userId) => paths.externalCpd(['details', externalCpdId, 'user', userId])
    },
    teams: {
      root: rootPaths.teams,
      allTeamMembers: paths.teams(['all-team-members']),
      unassignedTeamMembers: paths.teams(['unassigned-team-members']),
      teamMembers: (businessTeamId) => paths.teams(['team-members', businessTeamId]),
      teamMemberSettings: (teamId, userName, tab) => paths.teams([rootPaths.settings, 'team', teamId, 'user', userName, tab]),
      teamMemberProfiles: (teamId, userName) => paths.teams(['team', teamId, 'user', userName, "professional-profile"]),
      teamMemberCpdActivity: (teamId, userName) => paths.teams(['team', teamId, 'user', userName, "cpd-activity"]),
      teamMemberReport: (teamId, userId) => paths.teams(['team', teamId, 'user', userId, "progress-report"]),
      allTeamProgressReport: paths.teams(['team-progress-report']),
      singleTeamProgressReport: (teamId) => paths.teams(['team-progress-report', teamId]),
      allTeamMemberReport: (userId) => paths.teams(['user', userId, "progress-report"]),
      userSettings: (userName, tab) => paths.teams([rootPaths.settings, 'user', userName, tab]),
      userProfiles: (userName) => paths.teams(['user', userName, "professional-profile"]),
      userSnapshotReport: (userId, snapshotId) => paths.teams(['user', userId, 'snapshot', snapshotId, "progress-report"]),
      userCpdActivity: (userName) => paths.teams(['user', userName, "cpd-activity"]),
      teamSettings: (teamId, tab) => paths.teams([rootPaths.settings, 'team', teamId, tab]),
      teamEdit: (teamId) => paths.teams(['team', teamId, 'edit']),
      newTeam: paths.teams(['new'])
    }
  },
  onboarding: {
    root: rootPaths.onboarding,
    choosePlan: paths.onboarding(['choose-plan']),
    choosePlanIndustry: (industry) => paths.onboarding(['choose-plan', industry]),
    signup: paths.onboarding(['signup']),
    signupIndustry: (industry) => paths.onboarding(['signup', industry]),
    billing: paths.onboarding(['billing']),
    billingIndustry: (industry) => paths.onboarding(['billing', industry]),
    afsLicence: (industry) => paths.onboarding(['afs-licence', industry]),
    teamInvite: paths.onboarding(['team-invite']),
    welcome: paths.onboarding(['welcome']),
    cpdCategorys: paths.onboarding(['areas-of-advice']),
    cpdRequirements: paths.onboarding(['cpd-requirements']),
    cpdRequirementsIndustry: (industry) => paths.onboarding(['cpd-requirements', industry]),
    invitationSignUp: paths.onboarding(['invitation-signup']),
    acceptInvitation: paths.onboarding(['accept-invitation']),
    invalid: paths.onboarding(['404'])
  },
  publisherPortal: {
    root: rootPaths.publisherPortal,
    dashboard: rootPaths.publisherPortal,
    content: paths.publisherPortal(['content']),
    contentDetails: paths.publisherPortal(['content', 'details']),
    publishedEventEdit: (publishedEventId) => paths.publisherPortal(['event', 'details', publishedEventId]),
    publishedEventDuplicate: (publishedEventId) => paths.publisherPortal(['event', 'duplicate', publishedEventId]),
    publishedEventDashboard: paths.publisherPortal(['events']),
    publishedEvents: paths.publisherPortal(['all-events']),
    publishedEventDetails: paths.publisherPortal(['event', 'details']),
    contentQuestion: paths.publisherPortal(['content', 'question']),
    completedContent: paths.publisherPortal(['content', 'completed']),
    courseDashboard: paths.publisherPortal(['courses']),
    courses: paths.publisherPortal(['all-courses']),
    courseDetails: paths.publisherPortal(['course', 'details']),
    courseEdit: (publishedEventId) => paths.publisherPortal(['course', 'details', publishedEventId]),
  },
  adminPortal: {
    root: rootPaths.adminPortal,
    dashboard: rootPaths.adminPortal,
    publisher: paths.adminPortal(['publisher']),
    publisherEdit: (publisherId) => paths.adminPortal(['publisher', publisherId]),
    industry: paths.adminPortal(['industry']),
    industryAdd: paths.adminPortal(['add-industry']),
    industryEdit: (industryId) => paths.adminPortal(['industry', industryId]),
    qualificationType: paths.adminPortal(['qualification-type']),
    qualificationTypeEdit: (qualificationTypeId) => paths.adminPortal(['qualification-type', qualificationTypeId]),
    qualificationTypeAdd: paths.adminPortal(['add-qualification-type']),
    cpdCategory: paths.adminPortal(['cpd-category']),
    cpdCategoryEdit: (cpdCategoryId) => paths.adminPortal(['cpd-category', cpdCategoryId]),
    cpdCategoryAdd: paths.adminPortal(['add-cpd-category']),
    content: paths.adminPortal(['content']),
    contentRating: paths.adminPortal(['content-rating']),
    contentCompletion: paths.adminPortal(['content-completion']),
    userReport: (user, yearsFromNow) => paths.adminPortal(['report', 'user', user, 'year', yearsFromNow]),
    userSnapshotReport: (user, snapshot) => paths.adminPortal(['report', 'user', user, 'snapshot', snapshot]),
    businessReport: (business, yearsFromNow) => paths.adminPortal(['report', 'business', business, 'year', yearsFromNow]),
    testing: paths.adminPortal(['testing']),
    cpdPoint: paths.adminPortal(['cpd-point']),
    cpdAvailability: paths.adminPortal(['cpd-availability']),
    userEngagement: paths.adminPortal(['user-engagement']),
    adminTaskGroup: paths.adminPortal(['admin-task-group']),
    adminTaskGroupDetails: (adminTaskGroupId) => paths.adminPortal(['admin-task-group', adminTaskGroupId]),
    userCpdProgressReport: paths.adminPortal(['user-cpd-progress-report']),
    endOfYearReport: paths.adminPortal(['end-of-year-report'])
  },
  login: rootPaths.login,
  logout: rootPaths.logout,
  authenticate: rootPaths.authenticate,
  loggedOut: rootPaths.loggedOut,
  referral: rootPaths.referral,
  forgotPassword: rootPaths.forgotPassword,
  resetPassword: rootPaths.resetPassword
};



// WEBPACK FOOTER //
// ./src/constants/routes.js